import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Alert from '@amzn/meridian/alert/alert';
import { appLayoutContentId } from '@amzn/meridian/app-layout';
import Column from '@amzn/meridian/column';
import React, { createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
export var ScrollType;
(function (ScrollType) {
    ScrollType[ScrollType["SCROLL_TO_TOP"] = 0] = "SCROLL_TO_TOP";
    ScrollType[ScrollType["DISABLED"] = 1] = "DISABLED";
})(ScrollType || (ScrollType = {}));
const defaultAlertContext = {
    alert: null,
    showAlert: () => { },
    clearAlert: () => { },
};
export const AlertContext = createContext(defaultAlertContext);
// This is the hook into alert state. This MUST be called from components or hooks scoped inside the AlertProvider.
export const useAlert = () => React.useContext(AlertContext);
/**
 * AlertProvider manages global alert state for the application.
 *
 * Should be placed at the App level to persist across route changes.
 * Automatically clears alerts on navigation to prevent stale alerts.
 * This pairs with the PageAlert component, which renders at the top of page when alert state is non null.
 *
 * Usage:
 * - showAlert(alert) - displays an alert
 * - clearAlert() - manually clears the current alert
 * - alert state is automatically cleared on route changes
 */
export const AlertProvider = ({ children }) => {
    const [alert, setAlert] = useState(null);
    const location = useLocation();
    const clearAlert = () => {
        setAlert(null);
    };
    const showAlert = (alert) => {
        clearAlert();
        setAlert(alert);
    };
    // Clear alert on route change
    useEffect(() => {
        clearAlert();
    }, [location.pathname]);
    const contextValue = { alert, showAlert, clearAlert };
    const shouldRenderAlert = (alert === null || alert === void 0 ? void 0 : alert.type) && (alert.message || alert.title);
    // Scroll to top if alert is rendered as default behavior unless disabled.
    useEffect(() => {
        if (shouldRenderAlert && alert.scrollType !== ScrollType.DISABLED) {
            const scrollContainer = document.getElementById(appLayoutContentId);
            scrollContainer === null || scrollContainer === void 0 ? void 0 : scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [alert, shouldRenderAlert]);
    return (_jsxs(AlertContext.Provider, { value: contextValue, children: [shouldRenderAlert && (_jsx(Column, { spacingInset: "350 350 none 350", children: _jsx(Alert, { id: "page-alert", type: alert.type, children: _jsxs(Column, { children: [alert.title && _jsx("b", { children: alert.title }), _jsx("div", { children: alert.message })] }) }) })), children] }));
};
